import { BadRequestException, Injectable, Logger } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { MediaMigrationService } from './media-migration.service';
import { StorageConfigService } from './storage-config.service';
import { StorageQuotaService } from './storage-quota.service';
import { StorageUrlSweepService } from './storage-url-sweep.service';
import { isPlatformStorageConfigured } from './platform-storage.constants';

export interface RetirementPlan {
  organizationId: string;
  name: string;
  localAssets: number;
  localBytes: number;
  legacyUrlAudit: Record<string, number>;
}

export interface RetirementResult {
  organizationId: string;
  migrated: number;
  failed: number;
  jobId: string;
}

/**
 * Moves every organization off local disk and onto platform-managed storage
 * (docs/19-media-management-plan.md §13).
 *
 * This is the one migration that is not optional: primary media does not stay
 * on the application server. Everything else about switching storage is
 * co-existence (§5) — this is the exception, because the backend being left
 * behind is going away.
 *
 * Deliberately split into `plan()` and `run()`. The audit runs first and is
 * readable on its own, because the URL sweep needs to know what it is about to
 * touch: a `/api/uploads/...` string hiding in a theme settings JSON blob is a
 * broken image nobody notices for weeks, and enumerating those before the copy
 * starts is much cheaper than discovering them after.
 */
@Injectable()
export class LocalDiskRetirementService {
  private readonly logger = new Logger(LocalDiskRetirementService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly configs: StorageConfigService,
    private readonly migrations: MediaMigrationService,
    private readonly urlSweep: StorageUrlSweepService,
    private readonly quota: StorageQuotaService
  ) {}

  /** Dry run: what is still on disk, and where legacy URLs are stored. */
  async plan(): Promise<RetirementPlan[]> {
    const localConfigs = await this.prisma.merchantStorageConfig.findMany({
      where: { mode: 'local_disk', assetCount: { gt: 0 } },
      select: { id: true, organizationId: true, assetCount: true, assetBytes: true },
    });

    const plans: RetirementPlan[] = [];
    for (const config of localConfigs) {
      const org = await this.prisma.organization.findUnique({
        where: { id: config.organizationId },
        select: { name: true },
      });
      plans.push({
        organizationId: config.organizationId,
        name: org?.name ?? config.organizationId,
        localAssets: config.assetCount,
        localBytes: Number(config.assetBytes),
        legacyUrlAudit: await this.urlSweep.auditLegacyUrls(config.organizationId),
      });
    }
    return plans;
  }

  /**
   * Migrates one organization. Safe to re-run: the copy job resumes from where
   * it stopped, and assets already repointed are simply not selected again.
   */
  async retireOrganization(organizationId: string, actorId = 'system'): Promise<RetirementResult> {
    if (!isPlatformStorageConfigured()) {
      throw new BadRequestException(
        'Platform storage is not configured — set PLATFORM_R2_* before retiring local disk.'
      );
    }

    const source = await this.prisma.merchantStorageConfig.findFirst({
      where: { organizationId, mode: 'local_disk' },
      select: { id: true, assetCount: true },
    });
    if (!source) throw new BadRequestException(`No local-disk storage found for ${organizationId}`);

    // Creating the platform config also makes it the active write target, so
    // new uploads land in R2 from this moment even while the copy runs.
    const target = await this.configs.usePlatformStorage(organizationId, actorId);

    const job = await this.migrations.createJob({
      organizationId,
      sourceConfigId: source.id,
      targetConfigId: target.id,
      kind: 'local_to_r2',
      actorId,
    });

    await this.migrations.runToCompletion(job.id);
    const finished = await this.prisma.mediaMigrationJob.findUniqueOrThrow({ where: { id: job.id } });

    // The counters are maintained incrementally throughout; this squares them
    // against the registry once at the end, when it is cheap to be sure.
    await this.quota.recompute(organizationId);

    this.logger.log(
      `Local-disk retirement for ${organizationId}: ${finished.copiedCount} copied, ${finished.failedCount} failed`
    );

    return {
      organizationId,
      migrated: finished.copiedCount,
      failed: finished.failedCount,
      jobId: job.id,
    };
  }

  /**
   * Every organization, one at a time.
   *
   * Sequential on purpose: parallel migrations across orgs would multiply
   * memory (each object is read fully into a buffer) and give the source disk
   * a random-read pattern for no gain — the bottleneck is the upload side.
   */
  async retireAll(actorId = 'system'): Promise<RetirementResult[]> {
    const plans = await this.plan();
    const results: RetirementResult[] = [];

    for (const plan of plans) {
      try {
        results.push(await this.retireOrganization(plan.organizationId, actorId));
      } catch (err) {
        this.logger.error(`Retirement failed for ${plan.organizationId}: ${err}`);
        results.push({ organizationId: plan.organizationId, migrated: 0, failed: plan.localAssets, jobId: '' });
      }
    }

    const remaining = await this.prisma.mediaAsset.count({
      where: { storageConfig: { mode: 'local_disk' } },
    });
    if (remaining === 0) {
      this.logger.log(
        'No assets remain on local disk. /api/uploads can be removed after the fallback release, ' +
          'and the on-disk files deleted 30 days later.'
      );
    } else {
      this.logger.warn(`${remaining} asset(s) still on local disk — do NOT remove /api/uploads yet.`);
    }

    return results;
  }
}
